import React, { useState, useEffect } from 'react';
import api from '../services/api';
import '../styles/Pricing.css';

interface TowingPricing {
  bikeMinimumPrice: number;
  carMinimumPrice: number;
  baseDistanceKm: number;
  pricePerExtraKm: number;
}

interface FixedPricing {
  price: number;
  applicableVehicles?: string[];
}

interface ServicePricing {
  _id: string;
  serviceType: string;
  towingPricing?: TowingPricing;
  fixedPricing?: FixedPricing;
  commissionPercentage: number;
  isActive: boolean;
  lastUpdatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

const Pricing: React.FC = () => {
  const [pricingData, setPricingData] = useState<ServicePricing[]>([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<any>({});

  const serviceDisplayNames: { [key: string]: string } = {
    towing: 'Towing Service',
    petrol_delivery: 'Petrol Delivery',
    jumpstart: 'Jump Start',
    tire_change: 'Tire Change',
    lockout_service: 'Lockout Service',
    winch_out: 'Winch Out',
  };

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      setLoading(true);
      const response = await api.getAllPricing();
      if (response.success) {
        setPricingData(response.data);
      }
    } catch (error: any) {
      console.error('Error fetching pricing:', error);
      alert('Failed to fetch pricing data');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (service: ServicePricing) => {
    setEditMode(service.serviceType);
    setEditFormData({
      ...service,
    });
  };

  const handleCancelEdit = () => {
    setEditMode(null);
    setEditFormData({});
  };

  const handleSave = async (serviceType: string) => {
    try {
      const response = await api.updatePricing(serviceType, editFormData);
      if (response.success) {
        alert('Pricing updated successfully!');
        setEditMode(null);
        setEditFormData({});
        fetchPricing();
      }
    } catch (error: any) {
      console.error('Error updating pricing:', error);
      alert(error.response?.data?.message || 'Failed to update pricing');
    }
  };

  const handleInputChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setEditFormData((prev: any) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setEditFormData((prev: any) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const renderTowingPricingCard = (service: ServicePricing) => {
    const isEditing = editMode === service.serviceType;
    const data = isEditing ? editFormData : service;

    return (
      <div className="pricing-card">
        <div className="pricing-card-header">
          <h3>🚚 {serviceDisplayNames[service.serviceType]}</h3>
          {!isEditing ? (
            <button className="btn-edit" onClick={() => handleEdit(service)}>
              Edit
            </button>
          ) : (
            <div className="btn-group">
              <button className="btn-save" onClick={() => handleSave(service.serviceType)}>
                Save
              </button>
              <button className="btn-cancel" onClick={handleCancelEdit}>
                Cancel
              </button>
            </div>
          )}
        </div>

        <div className="pricing-details">
          <div className="pricing-row">
            <label>Bike Minimum Price (₹):</label>
            {isEditing ? (
              <input
                type="number"
                value={data.towingPricing?.bikeMinimumPrice || 0}
                onChange={(e) =>
                  handleInputChange('towingPricing.bikeMinimumPrice', parseInt(e.target.value))
                }
              />
            ) : (
              <span>₹{data.towingPricing?.bikeMinimumPrice}</span>
            )}
          </div>

          <div className="pricing-row">
            <label>Car Minimum Price (₹):</label>
            {isEditing ? (
              <input
                type="number"
                value={data.towingPricing?.carMinimumPrice || 0}
                onChange={(e) =>
                  handleInputChange('towingPricing.carMinimumPrice', parseInt(e.target.value))
                }
              />
            ) : (
              <span>₹{data.towingPricing?.carMinimumPrice}</span>
            )}
          </div>

          <div className="pricing-row">
            <label>Base Distance (km):</label>
            {isEditing ? (
              <input
                type="number"
                value={data.towingPricing?.baseDistanceKm || 0}
                onChange={(e) =>
                  handleInputChange('towingPricing.baseDistanceKm', parseInt(e.target.value))
                }
              />
            ) : (
              <span>{data.towingPricing?.baseDistanceKm} km</span>
            )}
          </div>

          <div className="pricing-row">
            <label>Price Per Extra Km (₹):</label>
            {isEditing ? (
              <input
                type="number"
                value={data.towingPricing?.pricePerExtraKm || 0}
                onChange={(e) =>
                  handleInputChange('towingPricing.pricePerExtraKm', parseInt(e.target.value))
                }
              />
            ) : (
              <span>₹{data.towingPricing?.pricePerExtraKm}/km</span>
            )}
          </div>

          <div className="pricing-row commission-row">
            <label>Platform Commission (%):</label>
            {isEditing ? (
              <input
                type="number"
                min="0"
                max="100"
                value={data.commissionPercentage || 0}
                onChange={(e) =>
                  handleInputChange('commissionPercentage', parseFloat(e.target.value))
                }
              />
            ) : (
              <span className="commission-value">{data.commissionPercentage || 0}%</span>
            )}
          </div>
          {isEditing && (
            <div className="pricing-note">
              <small>Commission deducted from driver earnings on job completion</small>
            </div>
          )}
        </div>
      </div>
    );
  };

  const getServiceIcon = (serviceType: string) => {
    const icons: { [key: string]: string } = {
      petrol_delivery: '⛽',
      jumpstart: '🔋',
      tire_change: '🛞',
      lockout_service: '🔑',
      winch_out: '⛓️',
    };
    return icons[serviceType] || '🔧';
  };

  const renderFixedPricingCard = (service: ServicePricing) => {
    const isEditing = editMode === service.serviceType;
    const data = isEditing ? editFormData : service;

    return (
      <div className="pricing-card">
        <div className="pricing-card-header">
          <h3>
            {getServiceIcon(service.serviceType)} {serviceDisplayNames[service.serviceType]}
          </h3>
          {!isEditing ? (
            <button className="btn-edit" onClick={() => handleEdit(service)}>
              Edit
            </button>
          ) : (
            <div className="btn-group">
              <button className="btn-save" onClick={() => handleSave(service.serviceType)}>
                Save
              </button>
              <button className="btn-cancel" onClick={handleCancelEdit}>
                Cancel
              </button>
            </div>
          )}
        </div>

        <div className="pricing-details">
          <div className="pricing-row">
            <label>Service Price (₹):</label>
            {isEditing ? (
              <input
                type="number"
                value={data.fixedPricing?.price || 0}
                onChange={(e) => handleInputChange('fixedPricing.price', parseInt(e.target.value))}
              />
            ) : (
              <span>₹{data.fixedPricing?.price}</span>
            )}
          </div>

          <div className="pricing-row commission-row">
            <label>Platform Commission (%):</label>
            {isEditing ? (
              <input
                type="number"
                min="0"
                max="100"
                value={data.commissionPercentage || 0}
                onChange={(e) =>
                  handleInputChange('commissionPercentage', parseFloat(e.target.value))
                }
              />
            ) : (
              <span className="commission-value">{data.commissionPercentage || 0}%</span>
            )}
          </div>

          {service.serviceType === 'winch_out' && (
            <div className="pricing-note">
              <small>Note: This service is only available for cars</small>
            </div>
          )}
          {isEditing && (
            <div className="pricing-note">
              <small>Commission deducted from driver earnings on job completion</small>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="pricing-container">
        <div className="loading">Loading pricing data...</div>
      </div>
    );
  }

  return (
    <div className="pricing-container">
      <div className="pricing-header">
        <h1>Service Pricing Management</h1>
        <p>Configure pricing for all services. Changes will be reflected immediately in the customer app.</p>
      </div>

      <div className="pricing-grid">
        {pricingData.map((service) => (
          <div key={service._id}>
            {service.serviceType === 'towing'
              ? renderTowingPricingCard(service)
              : renderFixedPricingCard(service)}
          </div>
        ))}
      </div>

      {pricingData.length === 0 && (
        <div className="no-data">
          <p>No pricing data available. Please seed the database with initial pricing.</p>
        </div>
      )}
    </div>
  );
};

export default Pricing;
